/**
 * Hypothetical Outcome Profile — payoff chart + scenario table. Ported from
 * the dane etf-website `components/buffer-scenario-table.tsx`.
 */

import type { FundDetail } from "@/data/fund-detail-types";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

type ScenarioType = "gain" | "loss" | "flat";

interface PayoffScenario {
  x: number;
  label: string;
  fundReturn: number;
  outcome: string;
  type: ScenarioType;
  rowIdx: number;
  isTbd?: boolean;
  refLabelOverride?: number;
  fundLabelOverride?: number;
}

function fundReturnAt(r: number, cap: number, buffer: number, bufferStart: number): number {
  if (r >= 0) return Math.min(r, cap);
  const loss = -r;
  if (bufferStart === 0) {
    if (loss <= buffer) return 0;
    return r + buffer;
  }
  const bufferEnd = bufferStart + buffer;
  if (loss <= bufferStart) return r;
  if (loss <= bufferEnd) return -bufferStart;
  return r + buffer;
}

interface ChartAxisOverride {
  strongNegRef?: number;
  strongNegFund?: number;
  cappedRef?: number;
}

function buildScenarios(
  buffer: number,
  bufferStart: number,
  cap: number,
  capTbd: boolean,
  chartAxisOverride?: ChartAxisOverride | null,
): PayoffScenario[] {
  const bufferEnd = bufferStart + buffer;
  const cappedOutcome = capTbd
    ? "Return capped at cap rate"
    : `Return capped at ${cap.toFixed(2)}%`;
  if (buffer >= 100) {
    return [
      {
        x: -100,
        label: "Strongly Negative",
        fundReturn: 0,
        outcome: "All losses absorbed by 100.00% buffer",
        type: "loss",
        rowIdx: 0,
      },
      {
        x: -10,
        label: "Negative",
        fundReturn: 0,
        outcome: "Loss fully absorbed by 100.00% buffer",
        type: "loss",
        rowIdx: 1,
      },
      { x: 0, label: "Flat", fundReturn: 0, outcome: "No gain or loss", type: "flat", rowIdx: 2 },
      {
        x: 5,
        label: "Positive (Pre-Cap)",
        fundReturn: 5,
        outcome: "Full upside captured below cap",
        type: "gain",
        rowIdx: 3,
      },
      {
        x: 25,
        label: "Positive (Capped)",
        fundReturn: cap,
        outcome: cappedOutcome,
        type: "gain",
        rowIdx: 4,
      },
    ];
  }
  if (bufferStart > 0) {
    return [
      {
        x: -45,
        label: "Strongly Negative",
        fundReturn: -(45 - buffer),
        outcome: `Losses beyond −${bufferEnd.toFixed(2)}% borne by investor`,
        type: "loss",
        rowIdx: 0,
      },
      {
        x: -10,
        label: "Negative",
        fundReturn: -bufferStart,
        outcome: `Loss between −${bufferStart.toFixed(2)}% and −${bufferEnd.toFixed(2)}% absorbed by buffer`,
        type: "loss",
        rowIdx: 1,
      },
      { x: 0, label: "Flat", fundReturn: 0, outcome: "No gain or loss", type: "flat", rowIdx: 2 },
      {
        x: 5,
        label: "Positive (Pre-Cap)",
        fundReturn: 5,
        outcome: "Full upside captured below cap",
        type: "gain",
        rowIdx: 3,
      },
      {
        x: 25,
        label: "Positive (Capped)",
        fundReturn: cap,
        outcome: cappedOutcome,
        type: "gain",
        rowIdx: 4,
      },
    ];
  }
  const strongNegOverrides =
    chartAxisOverride?.strongNegRef != null
      ? {
          refLabelOverride: chartAxisOverride.strongNegRef,
          ...(chartAxisOverride.strongNegFund != null
            ? { fundLabelOverride: chartAxisOverride.strongNegFund }
            : {}),
        }
      : {};
  const cappedOverrides =
    chartAxisOverride?.cappedRef != null ? { refLabelOverride: chartAxisOverride.cappedRef } : {};
  return [
    {
      x: -25,
      label: "Strongly Negative",
      fundReturn: -(25 - buffer),
      outcome: `Losses beyond ${buffer.toFixed(2)}% buffer borne by investor`,
      type: "loss",
      rowIdx: 0,
      ...strongNegOverrides,
    },
    {
      x: -10,
      label: "Negative",
      fundReturn: 0,
      outcome: `Loss fully absorbed by ${buffer.toFixed(2)}% buffer`,
      type: "loss",
      rowIdx: 1,
    },
    { x: 0, label: "Flat", fundReturn: 0, outcome: "No gain or loss", type: "flat", rowIdx: 2 },
    {
      x: 5,
      label: "Positive (Pre-Cap)",
      fundReturn: 5,
      outcome: "Full upside captured below cap",
      type: "gain",
      rowIdx: 3,
    },
    {
      x: 25,
      label: "Positive (Capped)",
      fundReturn: cap,
      outcome: cappedOutcome,
      type: "gain",
      rowIdx: 4,
      ...cappedOverrides,
    },
  ];
}

function isDarkMode(): boolean {
  return (
    typeof document === "undefined" ||
    document.documentElement.getAttribute("data-theme") !== "light"
  );
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function PayoffChart({
  fund,
  hoveredScenario,
  onHoverScenario,
}: {
  fund: FundDetail;
  hoveredScenario: number;
  onHoverScenario: (idx: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number | null>(null);
  const animProgressRef = useRef(0);
  const sizeRef = useRef({ W: 0, H: 0 });
  const hoverPosRef = useRef<{ x: number | null; y: number | null }>({ x: null, y: null });
  const hoveredScenarioRef = useRef<number>(-1);
  hoveredScenarioRef.current = hoveredScenario;
  const drawRef = useRef<() => void>(() => {});

  const [tooltip, setTooltip] = useState<{
    title: string;
    fundText: string;
    refText: string;
    x: number;
    y: number;
    visible: boolean;
  }>({ title: "", fundText: "", refText: "", x: 0, y: 0, visible: false });

  const capDetail = fund.bufferOutcomeDetails?.find(
    (d) => d.label.toLowerCase().includes("cap") && d.label.toLowerCase().includes("gross"),
  );
  const rawCap = fund.startingCapGrossPct ?? (capDetail ? Number.parseFloat(capDetail.value) : 0);
  const CAP_TBD = !rawCap || rawCap <= 0 || Number.isNaN(rawCap);
  const CAP = CAP_TBD ? 18 : rawCap;
  const BUFFER = fund.bufferLevel ?? 0;
  const BUFFER_START = fund.bufferStart ?? 0;
  const BUFFER_END = BUFFER_START + BUFFER;
  const REF_TICKER = fund.referenceTicker ?? "REF";

  let X_MIN: number;
  let X_MAX: number;
  if (BUFFER >= 100) {
    X_MIN = -100;
    X_MAX = 65;
  } else if (BUFFER_START > 0) {
    X_MIN = -60;
    X_MAX = 30;
  } else {
    X_MIN = -30;
    X_MAX = 30;
  }
  const Y_MIN = X_MIN;
  const Y_MAX = X_MAX;
  const STEP = 10;
  const PAD = { top: 20, right: 24, bottom: 44, left: 70 };

  const SCENARIOS: PayoffScenario[] = buildScenarios(
    BUFFER,
    BUFFER_START,
    CAP,
    CAP_TBD,
    fund.bufferConfig?.chartAxisOverride,
  );

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = wrap.getBoundingClientRect();
    const W = Math.round(rect.width);
    const H = Math.round(rect.height);
    if (W === sizeRef.current.W && H === sizeRef.current.H) return;
    sizeRef.current = { W, H };
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { W, H } = sizeRef.current;
    if (W === 0 || H === 0) return;
    const isMobile = W < 500;
    const mxLocal = hoverPosRef.current.x;
    const myLocal = hoverPosRef.current.y;
    const hoveredIdx = hoveredScenarioRef.current;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;

    const xToPixel = (v: number) => PAD.left + ((v - X_MIN) / (X_MAX - X_MIN)) * plotW;
    const yToPixel = (v: number) => PAD.top + ((Y_MAX - v) / (Y_MAX - Y_MIN)) * plotH;
    const pixelToX = (px: number) => X_MIN + ((px - PAD.left) / plotW) * (X_MAX - X_MIN);

    const dark = isDarkMode();
    const gridCol = dark ? "rgba(255,255,255,0.038)" : "rgba(0,0,0,0.035)";
    const zeroCol = dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)";
    const tickCol = dark ? "rgba(255,255,255,0.34)" : "rgba(0,0,0,0.32)";
    const labelCol = dark ? "rgba(255,255,255,0.30)" : "rgba(0,0,0,0.30)";

    ctx.clearRect(0, 0, W, H);

    for (let v = Y_MIN; v <= Y_MAX; v += STEP) {
      const py = Math.round(yToPixel(v)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(PAD.left, py);
      ctx.lineTo(PAD.left + plotW, py);
      ctx.strokeStyle = v === 0 ? zeroCol : gridCol;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    for (let v = X_MIN; v <= X_MAX; v += STEP) {
      const px = Math.round(xToPixel(v)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(px, PAD.top);
      ctx.lineTo(px, PAD.top + plotH);
      ctx.strokeStyle = v === 0 ? zeroCol : gridCol;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    const bxL = xToPixel(-BUFFER_END);
    let bxR = xToPixel(-BUFFER_START);
    let byT = yToPixel(-BUFFER_START);
    const byB = yToPixel(Y_MIN);
    if (BUFFER_START === 0) {
      bxR = xToPixel(0);
      byT = yToPixel(0);
    }
    const bufColTop = PAD.top;
    const bufColBot = PAD.top + plotH;
    const bZoneX = bxL;
    const bZoneW = bxR - bxL;
    const bZoneY = BUFFER_START > 0 ? byT : bufColTop;
    const bZoneH = BUFFER_START > 0 ? byB - byT : bufColBot - bufColTop;

    ctx.fillStyle = dark ? "rgba(255,85,0,0.14)" : "rgba(255,85,0,0.11)";
    ctx.fillRect(bZoneX, bZoneY, bZoneW, bZoneH);

    ctx.fillStyle = dark ? "rgba(255,85,0,0.07)" : "rgba(255,85,0,0.055)";
    ctx.fillRect(bZoneX + 2, bZoneY + 2, bZoneW - 4, bZoneH - 4);

    ctx.save();
    ctx.strokeStyle = dark ? "rgba(255,85,0,0.45)" : "rgba(200,60,0,0.45)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(Math.round(bxL) + 0.5, bufColTop);
    ctx.lineTo(Math.round(bxL) + 0.5, bufColBot);
    ctx.stroke();
    if (BUFFER_START > 0) {
      ctx.beginPath();
      ctx.moveTo(Math.round(bxR) + 0.5, bufColTop);
      ctx.lineTo(Math.round(bxR) + 0.5, bufColBot);
      ctx.stroke();
    }
    ctx.restore();

    if (BUFFER_START > 0) {
      const dxL = xToPixel(-BUFFER_START);
      const dxR = xToPixel(0);
      const dedGrad = ctx.createLinearGradient(0, PAD.top, 0, PAD.top + plotH);
      dedGrad.addColorStop(0, dark ? "rgba(255,60,60,0.025)" : "rgba(200,40,40,0.02)");
      dedGrad.addColorStop(1, dark ? "rgba(255,60,60,0.09)" : "rgba(200,40,40,0.08)");
      ctx.fillStyle = dedGrad;
      ctx.fillRect(dxL, PAD.top, dxR - dxL, plotH);
    }

    const capY = yToPixel(CAP);
    const capTop = yToPixel(Y_MAX);
    const capGrad = ctx.createLinearGradient(0, capTop, 0, capY);
    capGrad.addColorStop(0, dark ? "rgba(58,219,106,0.02)" : "rgba(40,160,80,0.02)");
    capGrad.addColorStop(1, dark ? "rgba(58,219,106,0.10)" : "rgba(40,160,80,0.08)");
    ctx.fillStyle = capGrad;
    ctx.fillRect(PAD.left, capTop, plotW, capY - capTop);

    const snapCapY = Math.round(capY) + 0.5;
    ctx.save();
    ctx.lineWidth = 6;
    ctx.strokeStyle = dark ? "rgba(58,219,106,0.08)" : "rgba(40,160,80,0.06)";
    ctx.beginPath();
    ctx.moveTo(PAD.left, snapCapY);
    ctx.lineTo(PAD.left + plotW, snapCapY);
    ctx.stroke();
    ctx.lineWidth = 2;
    ctx.strokeStyle = dark ? "rgba(58,219,106,0.70)" : "rgba(40,160,80,0.65)";
    ctx.beginPath();
    ctx.moveTo(PAD.left, snapCapY);
    ctx.lineTo(PAD.left + plotW, snapCapY);
    ctx.stroke();
    ctx.restore();

    if (BUFFER_START > 0) {
      ctx.save();
      ctx.setLineDash([5, 4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = dark ? "rgba(255,85,0,0.30)" : "rgba(200,60,0,0.25)";
      const dedLineY = Math.round(yToPixel(-BUFFER_START)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(bxL, dedLineY);
      ctx.lineTo(bxR, dedLineY);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    ctx.font = "500 9px 'Instrument Sans', sans-serif";
    ctx.textAlign = "left";
    const pillPad = 5;
    const pillH = 16;
    const pillR = 3;

    const capLabel = CAP_TBD ? "CAP TBD" : `CAP +${CAP.toFixed(2)}%`;
    const capLW = ctx.measureText(capLabel).width;
    const capPx = Math.round(PAD.left + 8);
    const capPy = Math.round(capY - 6);
    ctx.fillStyle = dark ? "rgba(58,219,106,0.12)" : "rgba(40,160,80,0.12)";
    roundRect(
      ctx,
      capPx - pillPad,
      capPy - pillH + 4,
      Math.round(capLW) + pillPad * 2,
      pillH,
      pillR,
    );
    ctx.fill();
    ctx.fillStyle = dark ? "rgba(58,219,106,0.85)" : "rgba(40,160,80,0.85)";
    ctx.fillText(capLabel, capPx, capPy);

    const bufLine1 = "BUFFER";
    const bufLine2 =
      BUFFER >= 100
        ? "100%"
        : BUFFER_START > 0
          ? `−${BUFFER_START.toFixed(2)}% to −${BUFFER_END.toFixed(2)}%`
          : `−${BUFFER.toFixed(2)}%`;
    ctx.font = "600 9px 'Instrument Sans', sans-serif";
    const bufLine1W = ctx.measureText(bufLine1).width;
    ctx.font = "500 8px 'Instrument Sans', sans-serif";
    const bufLine2W = ctx.measureText(bufLine2).width;
    const bufBlockW = Math.max(bufLine1W, bufLine2W);
    const bufBlockH = 24;
    const bufBlockX = Math.round((bxL + bxR) / 2 - bufBlockW / 2);
    const bufBlockY = Math.round(PAD.top + 8);
    ctx.fillStyle = dark ? "rgba(12,10,8,0.82)" : "rgba(250,248,244,0.94)";
    roundRect(
      ctx,
      bufBlockX - pillPad,
      bufBlockY - 1,
      Math.round(bufBlockW) + pillPad * 2,
      bufBlockH,
      pillR,
    );
    ctx.fill();
    ctx.fillStyle = dark ? "rgba(255,85,0,0.85)" : "rgba(200,60,0,0.85)";
    ctx.fillRect(bufBlockX - pillPad, bufBlockY - 1, 2, bufBlockH);
    ctx.font = "600 9px 'Instrument Sans', sans-serif";
    ctx.textAlign = "center";
    ctx.fillStyle = dark ? "rgba(255,85,0,0.85)" : "rgba(200,60,0,0.85)";
    ctx.fillText(bufLine1, Math.round(bufBlockX + bufBlockW / 2), bufBlockY + 9);
    ctx.font = "500 9px 'Instrument Sans', sans-serif";
    ctx.fillStyle = dark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.65)";
    ctx.fillText(bufLine2, Math.round(bufBlockX + bufBlockW / 2), bufBlockY + 21);

    {
      const zoneMidX = Math.round((bxL + bxR) / 2);
      const zoneMidY =
        BUFFER_START === 0
          ? Math.round(PAD.top + plotH * 0.72)
          : Math.round((yToPixel(-BUFFER_START) + yToPixel(Y_MIN)) / 2);
      ctx.save();
      ctx.translate(zoneMidX, zoneMidY);
      ctx.rotate(-Math.PI / 2);
      ctx.font = "700 11px 'F37 Bolton', sans-serif";
      (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = "0.08em";
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillStyle = dark ? "rgba(255,255,255,0.90)" : "rgba(180,50,0,0.85)";
      ctx.fillText("BUFFER ZONE", 0, 0);
      (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = "0px";
      ctx.restore();

      if (BUFFER_START > 0) {
        const dedLabel = `−${BUFFER_START.toFixed(2)}% FIRST LOSS`;
        const dedMidX = Math.round((xToPixel(-BUFFER_START) + xToPixel(0)) / 2);
        const dedMidY = Math.round(yToPixel(0) + 28);
        ctx.save();
        ctx.translate(dedMidX, dedMidY);
        ctx.rotate(-Math.PI / 2);
        ctx.font = "600 9px 'F37 Bolton', sans-serif";
        (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = "0.06em";
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        ctx.fillStyle = dark ? "rgba(255,120,100,0.80)" : "rgba(180,50,0,0.70)";
        ctx.fillText(dedLabel, 0, 0);
        (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = "0px";
        ctx.restore();
      }
    }

    const totalPts = X_MAX - X_MIN + 1;
    const drawCount = Math.round(animProgressRef.current * totalPts);

    ctx.save();
    ctx.beginPath();
    ctx.rect(PAD.left, PAD.top, plotW, plotH);
    ctx.clip();

    ctx.beginPath();
    ctx.strokeStyle = dark ? "rgba(255,255,255,0.52)" : "rgba(0,0,0,0.42)";
    ctx.lineWidth = 2;
    ctx.setLineDash([7, 5]);
    for (let i = 0; i < drawCount; i++) {
      const xv = X_MIN + i;
      const px = xToPixel(xv);
      const py = yToPixel(xv);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.strokeStyle = "#ff5500";
    ctx.lineWidth = 2.5;
    for (let i = 0; i < drawCount; i++) {
      const xv = X_MIN + i;
      const fv = fundReturnAt(xv, CAP, BUFFER, BUFFER_START);
      const px = xToPixel(xv);
      const py = yToPixel(fv);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.restore();

    ctx.font = "400 9px 'Geist', sans-serif";
    ctx.textAlign = "center";
    ctx.fillStyle = tickCol;
    for (let v = X_MIN; v <= X_MAX; v += STEP) {
      ctx.fillText(
        `${v > 0 ? "+" : ""}${v.toFixed(2)}%`,
        Math.round(xToPixel(v)),
        Math.round(PAD.top + plotH + 16),
      );
    }
    ctx.textAlign = "right";
    for (let v = Y_MIN; v <= Y_MAX; v += STEP) {
      ctx.fillText(
        `${v > 0 ? "+" : ""}${v.toFixed(2)}%`,
        Math.round(PAD.left - 6),
        Math.round(yToPixel(v) + 3),
      );
    }

    ctx.font = "500 11px 'F37 Bolton', sans-serif";
    ctx.fillStyle = labelCol;
    ctx.textAlign = "center";
    ctx.fillText("Reference Asset Return", Math.round(PAD.left + plotW / 2), Math.round(H - 4));
    ctx.save();
    ctx.translate(14, Math.round(PAD.top + plotH / 2));
    ctx.rotate(-Math.PI / 2);
    ctx.fillText("Fund Return", 0, 0);
    ctx.restore();

    if (animProgressRef.current >= 1) {
      for (let si = 0; si < SCENARIOS.length; si++) {
        const sc = SCENARIOS[si]!;
        if (sc.isTbd) continue;
        if (sc.label === "Negative" && BUFFER >= 100) continue;
        const sx = xToPixel(sc.x);
        const sy = yToPixel(sc.fundReturn);
        const refSy = yToPixel(sc.x);
        const isHovered = si === hoveredIdx;
        const r = isHovered ? 8 : 6;
        const dotCol =
          sc.type === "gain"
            ? dark
              ? "#3adb6a"
              : "#1a9640"
            : sc.type === "loss"
              ? "#ff5500"
              : dark
                ? "rgba(255,255,255,0.6)"
                : "rgba(0,0,0,0.4)";
        const refDotCol = dark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.14)";

        if (isHovered) {
          ctx.beginPath();
          ctx.arc(sx, sy, r + 6, 0, Math.PI * 2);
          ctx.fillStyle =
            sc.type === "gain"
              ? dark
                ? "rgba(58,219,106,0.10)"
                : "rgba(26,150,64,0.08)"
              : sc.type === "loss"
                ? "rgba(255,85,0,0.10)"
                : dark
                  ? "rgba(255,255,255,0.07)"
                  : "rgba(0,0,0,0.045)";
          ctx.fill();
        }

        if (sc.x !== sc.fundReturn) {
          ctx.setLineDash([2, 3]);
          ctx.beginPath();
          ctx.moveTo(sx, refSy);
          ctx.lineTo(sx, sy);
          ctx.strokeStyle = dark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)";
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.arc(sx, refSy, 3.5, 0, Math.PI * 2);
          ctx.fillStyle = refDotCol;
          ctx.fill();
          ctx.strokeStyle = dark ? "#1a1510" : "#faf9f7";
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fillStyle = dotCol;
        ctx.fill();
        ctx.strokeStyle = dark ? "#1a1510" : "#faf9f7";
        ctx.lineWidth = 2.5;
        ctx.stroke();

        const isCapped = sc.label === "Positive (Capped)";
        const isPreCap = sc.label === "Positive (Pre-Cap)";
        const isStrongNeg = sc.label === "Strongly Negative";
        const isNeg = sc.label === "Negative";
        const isFlat = sc.label === "Flat";

        const fundValStr =
          CAP_TBD && isCapped
            ? "TBD"
            : `${sc.fundReturn > 0 ? "+" : ""}${sc.fundReturn.toFixed(2)}%`;
        const refValStr = `${sc.x > 0 ? "+" : ""}${sc.x.toFixed(2)}%`;

        const borderLeftCol =
          sc.type === "gain"
            ? dark
              ? "#3adb6a"
              : "#1a9640"
            : sc.type === "loss"
              ? "#ff5500"
              : dark
                ? "rgba(255,255,255,0.55)"
                : "rgba(0,0,0,0.45)";
        const fundValCol =
          sc.type === "gain"
            ? dark
              ? "#3adb6a"
              : "#1a9640"
            : sc.type === "loss"
              ? "#ff5500"
              : dark
                ? "rgba(255,255,255,0.85)"
                : "rgba(0,0,0,0.65)";

        const nameFontPx = isMobile ? 7 : 9;
        const valFontPx = isMobile ? 8 : 10;
        const subFontPx = isMobile ? 6 : 8;
        const refFontPx = isMobile ? 7 : 9;
        ctx.font = `600 ${nameFontPx}px 'F37 Bolton', 'Geist', sans-serif`;
        const w1 = ctx.measureText(sc.label).width;
        ctx.font = `700 ${valFontPx}px 'F37 Bolton', 'Geist', sans-serif`;
        const fundValW = ctx.measureText(fundValStr).width;
        ctx.font = `500 ${refFontPx}px 'Geist', sans-serif`;
        const refValW = ctx.measureText(refValStr).width;
        const labelColW = isMobile ? 14 : 20;
        const rowGap = isMobile ? 3 : 5;
        const w2 = labelColW + rowGap + fundValW;
        const w3 = labelColW + rowGap + refValW;

        const cardPadL = isMobile ? 5 : 8;
        const cardPadR = isMobile ? 4 : 7;
        const cardPadT = isMobile ? 2 : 4;
        const cardPadB = isMobile ? 2 : 4;
        const cardW = Math.ceil(Math.max(w1, w2, w3) + cardPadL + cardPadR);
        const rowLineH = isMobile ? 9 : 11;
        const cardH = nameFontPx + 2 + rowLineH + rowLineH + cardPadT + cardPadB;

        const GAP = isMobile ? 7 : 12;
        const R = isMobile ? 5 : 7;
        let pillLeft: number;
        let pillTop: number;
        if (BUFFER >= 100) {
          if (isStrongNeg) {
            pillLeft = sx + R + GAP;
            pillTop = sy + R + GAP + 10;
          } else if (isNeg) {
            pillLeft = PAD.left + 4;
            pillTop = sy + R + GAP;
          } else if (isFlat) {
            pillLeft = sx + R + GAP;
            pillTop = sy + R + GAP;
          } else if (isPreCap) {
            pillLeft = sx + R + GAP;
            pillTop = sy - R - GAP - cardH - 24;
          } else {
            pillLeft = sx + R + GAP;
            pillTop = sy + R + GAP + 10;
          }
        } else {
          if (isStrongNeg) {
            pillLeft = Math.max(PAD.left + 4, sx - cardW - GAP);
            pillTop = sy - R - GAP - cardH;
          } else if (isNeg) {
            pillLeft = Math.max(PAD.left + 4, xToPixel(-BUFFER_END) - cardW - GAP);
            pillTop = yToPixel(0) - R - GAP - cardH;
          } else if (isFlat) {
            pillLeft = sx + R + GAP;
            pillTop = sy + R + GAP;
          } else if (isPreCap) {
            pillLeft = sx + R + GAP;
            pillTop = sy - R - GAP - cardH - 24;
          } else {
            pillLeft = sx - cardW - GAP;
            pillTop = sy + R + GAP + 10;
          }
        }
        const cardX = Math.round(pillLeft);
        const cardY = Math.round(pillTop);

        ctx.save();
        roundRect(ctx, cardX, cardY, cardW, cardH, 3);
        ctx.fillStyle = dark ? "rgba(12,10,8,0.82)" : "rgba(250,248,244,0.94)";
        ctx.fill();
        ctx.restore();
        ctx.save();
        ctx.fillStyle = borderLeftCol;
        ctx.fillRect(cardX, cardY, 2, cardH);
        ctx.restore();

        ctx.save();
        ctx.font = `600 ${nameFontPx}px 'F37 Bolton', 'Geist', sans-serif`;
        (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = "0.04em";
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        ctx.fillStyle = dark ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.78)";
        ctx.fillText(sc.label, cardX + cardPadL, cardY + cardPadT + nameFontPx);
        (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = "0px";
        ctx.restore();

        const row1Y = cardY + cardPadT + nameFontPx + 2 + valFontPx;
        ctx.save();
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        ctx.font = `${subFontPx}px 'Geist', sans-serif`;
        ctx.fillStyle = dark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.40)";
        ctx.fillText("Fund", cardX + cardPadL, row1Y);
        ctx.font = `700 ${valFontPx}px 'F37 Bolton', 'Geist', sans-serif`;
        ctx.fillStyle = fundValCol;
        ctx.fillText(fundValStr, cardX + cardPadL + labelColW + rowGap, row1Y);
        ctx.restore();

        const row2Y = row1Y + rowLineH;
        ctx.save();
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        ctx.font = `${subFontPx}px 'Geist', sans-serif`;
        ctx.fillStyle = dark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.40)";
        ctx.fillText(REF_TICKER, cardX + cardPadL, row2Y);
        ctx.font = `500 ${refFontPx}px 'Geist', sans-serif`;
        ctx.fillStyle = dark ? "rgba(255,255,255,0.78)" : "rgba(0,0,0,0.62)";
        ctx.fillText(refValStr, cardX + cardPadL + labelColW + rowGap, row2Y);
        ctx.restore();

        {
          const lx = pillLeft > sx ? pillLeft : pillLeft + cardW;
          const ly = pillTop > sy ? pillTop : pillTop + cardH;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(Math.round(lx) + 0.5, Math.round(ly) + 0.5);
          ctx.lineTo(Math.round(sx) + 0.5, Math.round(sy) + 0.5);
          ctx.strokeStyle = dark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.16)";
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 3]);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        }
      }
    }

    void mxLocal;
    void myLocal;
    void pixelToX;
  }, [
    BUFFER,
    BUFFER_START,
    BUFFER_END,
    CAP,
    CAP_TBD,
    REF_TICKER,
    X_MIN,
    X_MAX,
    Y_MIN,
    Y_MAX,
    PAD,
    SCENARIOS,
  ]);

  drawRef.current = draw;

  useEffect(() => {
    animProgressRef.current = 0;
    let start: number | null = null;
    const animate = (ts: number) => {
      if (start === null) start = ts;
      const elapsed = ts - start;
      const dur = 1200;
      animProgressRef.current = Math.min(1, elapsed / dur);
      drawRef.current();
      if (animProgressRef.current < 1) {
        animRef.current = requestAnimationFrame(animate);
      }
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current !== null) cancelAnimationFrame(animRef.current);
    };
  }, []);

  useEffect(() => {
    resize();
    drawRef.current();
    const onResize = () => {
      resize();
      drawRef.current();
    };
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(() => {
      resize();
      drawRef.current();
    });
    if (wrapRef.current) ro.observe(wrapRef.current);
    const observer = new MutationObserver(() => drawRef.current());
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      observer.disconnect();
    };
  }, [resize]);

  useEffect(() => {
    drawRef.current();
  }, [hoveredScenario]);

  const lastHoveredScenarioRef = useRef<number>(-1);
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const wrap = wrapRef.current;
      if (!wrap || animProgressRef.current < 1) return;
      const rect = wrap.getBoundingClientRect();
      const W = rect.width;
      const H = rect.height;
      const plotW = W - PAD.left - PAD.right;
      const plotH = H - PAD.top - PAD.bottom;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      if (mx < PAD.left || mx > W - PAD.right || my < PAD.top || my > PAD.top + plotH) {
        hoverPosRef.current = { x: null, y: null };
        if (lastHoveredScenarioRef.current !== -1) {
          lastHoveredScenarioRef.current = -1;
          onHoverScenario(-1);
        }
        setTooltip((t) => (t.visible ? { ...t, visible: false } : t));
        wrap.style.cursor = "";
        drawRef.current();
        return;
      }

      const xToPixel = (v: number) => PAD.left + ((v - X_MIN) / (X_MAX - X_MIN)) * plotW;
      const yToPixel = (v: number) => PAD.top + ((Y_MAX - v) / (Y_MAX - Y_MIN)) * plotH;

      let nearIdx = -1;
      for (let i = 0; i < SCENARIOS.length; i++) {
        const sc = SCENARIOS[i]!;
        if (sc.isTbd) continue;
        const sx = xToPixel(sc.x);
        const sy = yToPixel(sc.fundReturn);
        const dx = mx - sx;
        const dy = my - sy;
        if (Math.sqrt(dx * dx + dy * dy) < 20) {
          nearIdx = i;
          break;
        }
      }

      wrap.style.cursor = nearIdx >= 0 ? "pointer" : "";

      hoverPosRef.current = { x: mx, y: my };
      if (lastHoveredScenarioRef.current !== nearIdx) {
        lastHoveredScenarioRef.current = nearIdx;
        onHoverScenario(nearIdx);
      }
      drawRef.current();
    },
    [SCENARIOS, X_MIN, X_MAX, Y_MIN, Y_MAX, PAD, onHoverScenario],
  );

  const handleMouseLeave = useCallback(() => {
    hoverPosRef.current = { x: null, y: null };
    if (lastHoveredScenarioRef.current !== -1) {
      lastHoveredScenarioRef.current = -1;
      onHoverScenario(-1);
    }
    setTooltip((t) => (t.visible ? { ...t, visible: false } : t));
    if (wrapRef.current) wrapRef.current.style.cursor = "";
    drawRef.current();
  }, [onHoverScenario]);

  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative h-[380px] max-md:h-[260px] max-[480px]:h-[220px] mx-10 max-md:mx-4 max-[480px]:mx-3 mt-5 mb-4 border border-[var(--etf-border)] rounded overflow-hidden"
    >
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={`Payoff profile diagram showing ${fund.ticker} hypothetical returns versus ${REF_TICKER}`}
        className="block"
        style={{ width: "100%", height: "100%" }}
      />
      <div
        ref={tooltipRef}
        className={cn(
          "absolute pointer-events-none bg-[var(--etf-black2)] border border-[var(--etf-border)] rounded-[3px] px-3.5 py-2.5 z-10 whitespace-nowrap transition-opacity duration-[120ms]",
          tooltip.visible ? "opacity-100" : "opacity-0",
        )}
        style={{ left: tooltip.x, top: tooltip.y }}
      >
        <div className="font-[family-name:var(--font-geist)] text-[10px] text-[var(--etf-white3)] mb-1.5 tracking-[0.04em]">
          {tooltip.title}
        </div>
        <div className="flex items-center gap-2 font-[family-name:var(--font-geist)] text-[11px] text-[var(--etf-white)] leading-[1.7]">
          <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-[#ff5500]" />
          <span>{tooltip.fundText}</span>
        </div>
        <div className="flex items-center gap-2 font-[family-name:var(--font-geist)] text-[11px] text-[var(--etf-white)] leading-[1.7]">
          <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-[var(--etf-white3)] opacity-50" />
          <span>{tooltip.refText}</span>
        </div>
      </div>
    </div>
  );
}

export function BufferScenarioTable({ fund }: { fund: FundDetail }) {
  const refTicker = fund.referenceTicker ?? "REF";
  const buffer = fund.bufferLevel ?? 0;
  const bufStart = fund.bufferStart ?? 0;
  const capDetail = fund.bufferOutcomeDetails?.find(
    (d) => d.label.toLowerCase().includes("cap") && d.label.toLowerCase().includes("gross"),
  );
  const rawCap = fund.startingCapGrossPct ?? (capDetail ? Number.parseFloat(capDetail.value) : 0);
  const capTbd = !rawCap || rawCap <= 0 || Number.isNaN(rawCap);
  const cap = capTbd ? 0 : rawCap;
  const scenarios = buildScenarios(
    buffer,
    bufStart,
    cap,
    capTbd,
    fund.bufferConfig?.chartAxisOverride,
  );

  const [hoveredScenario, setHoveredScenario] = useState<number>(-1);

  const formatPct = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(2)}%`;

  return (
    <section
      id="sec-scenarios"
      data-fund-section="bufferScenarios bufferConfig"
      className="border-b border-[var(--etf-border)] pb-[18px]"
    >
      <div className="flex items-baseline justify-between px-10 max-md:px-4 py-6 border-b border-[var(--etf-border)] gap-4 max-md:flex-col max-md:items-start max-md:gap-3">
        <div>
          <h2 className="font-[family-name:var(--font-f37bolton)] text-[24px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3">
            <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)]" />
            Hypothetical Outcome Profile
          </h2>
          <p className="font-[family-name:var(--font-geist)] text-[12px] text-[var(--etf-white3)] tracking-[0.08em] mt-1 pl-[15px]">
            End-of-Outcome Period Return Potential &middot; Before Fees &amp; Expenses
          </p>
        </div>
        <div className="flex gap-4 self-end max-md:self-start max-md:pl-[15px] flex-wrap">
          <div className="flex items-center gap-1.5 font-[family-name:var(--font-geist)] text-[11px] text-[var(--etf-white3)]">
            <span className="w-4 h-[2.5px] rounded-sm shrink-0 bg-[var(--etf-orange)]" />
            <span>Buffer ETF Return</span>
          </div>
          <div className="flex items-center gap-1.5 font-[family-name:var(--font-geist)] text-[11px] text-[var(--etf-white3)]">
            <span className="w-4 h-0 shrink-0 border-t-2 border-dashed border-[var(--etf-white3)] opacity-40" />
            <span>Direct {refTicker} Return</span>
          </div>
        </div>
      </div>
      <PayoffChart
        fund={fund}
        hoveredScenario={hoveredScenario}
        onHoverScenario={setHoveredScenario}
      />
      <div className="overflow-x-auto px-10 max-md:px-4 max-[480px]:px-3">
        <table className="w-full border-collapse border border-[var(--etf-border)] rounded">
          <thead>
            <tr>
              <th className="font-[family-name:var(--font-geist)] text-[10px] tracking-[0.12em] uppercase text-[var(--etf-white3)] px-4 max-md:px-3 py-2.5 text-left border-b border-[var(--etf-border)] bg-[var(--etf-black2)] whitespace-nowrap font-normal">
                Scenario
              </th>
              <th className="font-[family-name:var(--font-geist)] text-[10px] tracking-[0.12em] uppercase text-[var(--etf-white3)] px-4 max-md:px-3 py-2.5 text-right border-b border-[var(--etf-border)] bg-[var(--etf-black2)] whitespace-nowrap font-normal">
                {refTicker} Return
              </th>
              <th className="font-[family-name:var(--font-geist)] text-[10px] tracking-[0.12em] uppercase text-[var(--etf-white3)] px-4 max-md:px-3 py-2.5 text-right border-b border-[var(--etf-border)] bg-[var(--etf-black2)] whitespace-nowrap font-normal">
                Fund Return
              </th>
              <th className="font-[family-name:var(--font-geist)] text-[10px] tracking-[0.12em] uppercase text-[var(--etf-white3)] px-4 max-md:px-3 py-2.5 text-left border-b border-[var(--etf-border)] bg-[var(--etf-black2)] whitespace-nowrap font-normal max-[480px]:hidden">
                Outcome
              </th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s, i) => {
              const isHighlighted = hoveredScenario === i;
              const refDisplay = s.isTbd ? "TBD" : formatPct(s.refLabelOverride ?? s.x);
              const fundDisplay = s.isTbd
                ? "TBD"
                : capTbd && s.label === "Positive (Capped)"
                  ? "TBD"
                  : formatPct(s.fundLabelOverride ?? s.fundReturn);
              return (
                <tr
                  key={s.label}
                  onMouseEnter={() => setHoveredScenario(i)}
                  onMouseLeave={() => setHoveredScenario(-1)}
                  className={cn(
                    "transition-[background] duration-[120ms] cursor-pointer",
                    isHighlighted ? "bg-[rgba(255,85,0,0.06)]" : "hover:bg-white/[0.03]",
                    s.type === "loss" &&
                      "[&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-[var(--etf-red)] [&>td:first-child]:pl-[13px]",
                    s.type === "gain" &&
                      "[&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-[var(--etf-green)] [&>td:first-child]:pl-[13px]",
                    s.type === "flat" &&
                      "[&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-[var(--etf-white3)] [&>td:first-child]:pl-[13px]",
                  )}
                >
                  <td className="font-[family-name:var(--font-geist)] text-[13px] max-md:text-xs font-medium px-4 max-md:px-3 py-3.5 max-md:py-2.5 border-b border-[var(--etf-border2)] text-[var(--etf-white)]">
                    {s.label}
                  </td>
                  <td
                    className={cn(
                      "font-[family-name:var(--font-geist)] text-[13px] max-md:text-xs font-medium px-4 max-md:px-3 py-3.5 max-md:py-2.5 border-b border-[var(--etf-border2)] text-right",
                      s.isTbd && "text-[var(--etf-white3)]",
                      !s.isTbd && s.type === "loss" && "text-[#ff4f4f]",
                      !s.isTbd && s.type === "gain" && "text-[#3adb6a]",
                      !s.isTbd && s.type === "flat" && "text-[var(--etf-white)]",
                    )}
                  >
                    {refDisplay}
                  </td>
                  <td
                    className={cn(
                      "font-[family-name:var(--font-geist)] text-[13px] max-md:text-xs font-medium px-4 max-md:px-3 py-3.5 max-md:py-2.5 border-b border-[var(--etf-border2)] text-right",
                      s.isTbd && "text-[var(--etf-white3)]",
                      !s.isTbd && s.type === "loss" && "text-[#ff4f4f]",
                      !s.isTbd && s.type === "gain" && "text-[#3adb6a]",
                      !s.isTbd && s.type === "flat" && "text-[var(--etf-white)]",
                    )}
                  >
                    {fundDisplay}
                  </td>
                  <td className="font-[family-name:var(--font-geist)] text-[11px] max-md:text-[10px] max-[480px]:hidden font-normal px-4 max-md:px-3 py-3.5 max-md:py-2.5 border-b border-[var(--etf-border2)] text-[var(--etf-white3)] leading-[1.4]">
                    {s.outcome}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="font-[family-name:var(--font-geist)] text-[11.5px] font-medium text-[var(--etf-white)] mt-3.5 px-10 max-md:px-4 max-[480px]:px-3 leading-normal">
        <strong>Hypothetical illustration only.</strong> Performance outcomes shown are hypothetical
        and based on certain assumptions that may or may not occur. There is no guarantee that the
        Fund will be successful in providing these investment outcomes for any Outcome Period. The
        illustration assumes an investor purchases Fund shares on the first day of the Outcome
        Period and holds through the last day. Figures are shown before Fund fees and expenses. Fees
        and expenses will reduce Fund returns and may reduce the effective cap and buffer
        experienced by shareholders. The cap is determined at the start of each Outcome Period and
        will vary. Investors who buy or sell shares outside the full Outcome Period may experience
        materially different returns, including reduced or no remaining buffer, lower upside
        potential, or losses.
      </p>
    </section>
  );
}
